/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { translateError } from "./errors";

type ActionWithConfirmOptions<T = any> = {
  confirmMessage: string;
  successMessage: string;
  action: () => Promise<T>;
  successAction?: (res: T) => void;
  finallyAction?: () => void;
  skipConfirm?: boolean;
  canExecute?: () => boolean;
};

export const useActionWithConfirm = () => {
  const { t } = useTranslation();

  return useCallback(
    <T>({
      confirmMessage,
      successMessage,
      action,
      successAction,
      finallyAction,
      skipConfirm = false,
      canExecute = () => true,
    }: ActionWithConfirmOptions<T>) => {
      const executeAction = () => {
        if (!canExecute()) {
          finallyAction?.();
          return Promise.resolve(undefined);
        }
        const progressToastId = toast.loading(t("ui.processing"));
        return action()
          .then((res) => {
            toast.success(successMessage);
            return successAction ? successAction(res) : res;
          })
          .catch((e: unknown) => {
            if (e instanceof Error) {
              toast.error(translateError(t, e));
            }
          })
          .finally(() => {
            toast.dismiss(progressToastId);
            finallyAction && finallyAction();
          });
      };

      if (skipConfirm) {
        return executeAction();
      }

      // The dialog carried its title separately, so it has to be folded into the body here.
      if (!window.confirm(`${t("feature.common.confirmTitle")}\n\n${confirmMessage}`)) {
        finallyAction?.();
        return Promise.resolve(undefined);
      }

      // `window.confirm` blocks rendering, so lifecycle and authorization updates that landed
      // while it was open have not been applied yet. Yield once so the guard inside
      // `executeAction` reads them instead of the state from before the prompt.
      return (async () => {
        await new Promise((resolve) => setTimeout(resolve, 0));
        return executeAction();
      })();
    },
    [t],
  );
};
